/**
 * The staff support console at `/support` (components/support/).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow. Neither
 * object may be `as const`: `ar` is typed against `en`, and literal types
 * would make it have to repeat the English words.
 *
 * ## Why a staff-only screen is bilingual at all
 *
 * Because the rule in docs/i18n.md has no clause about who is reading. There
 * are no English-only strings in this product, and a console shipped in one
 * language would be the first — which in practice means the second, and then
 * the fifth, because the argument that won here wins everywhere. The people
 * who will actually work this desk read Arabic, and the conversations they
 * are answering are in Arabic roughly as often as not; a console that made
 * them read English while typing Arabic would be adding a translation step to
 * every reply.
 *
 * ## Register
 *
 * This is the one surface in the product not written for a learner, and the
 * voice changes accordingly: it is a tool, addressed to a colleague, and it
 * does not explain itself or encourage anybody. Short imperatives — `استلم`,
 * `أعده`, `أُغلقت` — and no reassurance. The learner-facing words it prints
 * *about* somebody, though, keep the course's glossary exactly: مستوى, نجمة,
 * نقاط الخبرة, محطة, فريق. An agent reading `محطة` here and a learner reading
 * `محطة` on their own record have to be looking at the same thing.
 *
 * ## The sentences that count things
 *
 * The queue's three counts, an agent's load, and everything on the customer
 * panel that is a number. Arabic has six plural categories to English's two
 * and the dual is obligatory, so "2 waiting" is not "waiting" with a 2 in
 * front of it. Every one goes through `plural`, whose six forms are written
 * out per language rather than assembled from a stem — the same helper, with
 * the same argument behind it, as the one at the top of strings/chat.ts.
 *
 * ## What is deliberately not in here
 *
 * No word for hiding a message, banning a tag or purging a room. The console
 * cannot do any of those and the vocabulary should not imply that the next
 * release will: moderation is `scripts/chat-moderate.mjs` at a terminal, by
 * somebody who went and got the admin token. The one concession is a label
 * over the customer's chat tag, and it says what the tag is *for* elsewhere
 * rather than offering to use it here.
 */
import { SUPPORT_LIMITS, type SupportTopic } from '@engineer-os/chat';

/* ── Arabic agreement ─────────────────────────────────────────────────────── */

interface Forms {
  /** Nothing. Arabic negates rather than counting: `لا شيء ينتظر`. */
  zero: string;
  /** One. The thing is named and the numeral is dropped: `محادثة واحدة`. */
  one: string;
  /** Two. The dual, never `2` in front of a plural: `محادثتان`. */
  two: string;
  /** 3–10, where the counted noun is a plural in the genitive. */
  few: string;
  /** 11–99, where it goes back to an accusative singular: `15 محادثةً`. */
  many: string;
  /** 100, 101, 1000 … — the singular again, and the CLDR catch-all. */
  other: string;
}

/**
 * Pick the Arabic form a count agrees with. CLDR's rule for `ar`, all six of
 * it, copied from strings/chat.ts rather than imported: that file's helper is
 * private to it, and a shared one would be a module two domains have to agree
 * about before either can add a sentence.
 */
function plural(n: number, forms: Forms): string {
  const rest = n % 100;
  if (n === 0) return forms.zero;
  if (n === 1) return forms.one;
  if (n === 2) return forms.two;
  if (rest >= 3 && rest <= 10) return forms.few;
  if (rest >= 11 && rest <= 99) return forms.many;
  return forms.other;
}

/* ── How long something has been waiting ──────────────────────────────────── */

/**
 * The one relative time on the page, and the reason it is a function per
 * language rather than a formatter plus a unit.
 *
 * An agent picking the next conversation is choosing on exactly this number,
 * so it has to read at a glance: minutes while it is minutes, hours once it
 * stops being, days after that. Each language decides its own thresholds'
 * wording and its own agreement, which is what a shared `Intl.RelativeTimeFormat`
 * with a hand-picked unit could not do — the Arabic dual alone breaks it.
 */
function waitedEn(minutes: number): string {
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour' : `${hours} hours`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day' : `${days} days`;
}

function waitedAr(minutes: number): string {
  if (minutes < 1) return 'الآن';
  if (minutes < 60) {
    return plural(minutes, {
      zero: 'الآن',
      one: 'دقيقة',
      two: 'دقيقتان',
      few: `${minutes} دقائق`,
      many: `${minutes} دقيقةً`,
      other: `${minutes} دقيقة`,
    });
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return plural(hours, {
      zero: 'الآن',
      one: 'ساعة',
      two: 'ساعتان',
      few: `${hours} ساعات`,
      many: `${hours} ساعةً`,
      other: `${hours} ساعة`,
    });
  }
  const days = Math.floor(hours / 24);
  return plural(days, {
    zero: 'الآن',
    one: 'يوم',
    two: 'يومان',
    few: `${days} أيام`,
    many: `${days} يومًا`,
    other: `${days} يوم`,
  });
}

/* ── The catalogue ────────────────────────────────────────────────────────── */

export const en = {
  /* --- The page itself --------------------------------------------------- */
  title: 'Support desk',
  kicker: 'Staff',
  /**
   * What the three-pane strip is, for a screen reader.
   *
   * It labels the tab list that chooses between the panes below the
   * breakpoint, which is the one control on the page whose purpose is not
   * legible from its three one-word buttons.
   */
  landmark: 'The support desk: the queue, the conversation, and who you are talking to.',

  /* --- Signed out, or not on the roster ---------------------------------- *
   * One page for both, because the API answers 404 for both and this screen
   * must not be the thing that tells the two apart. It says what the address
   * is and offers the one action that could change the answer. It does not
   * say "you are not staff", it does not say a roster exists, and it does not
   * change its wording after a sign-in that did not help. */
  closed: {
    title: 'Support desk',
    lede: 'This address is where the people who answer questions about this course read them.',
    signIn: 'Sign in',
    busy: 'Signing in…',
    note: 'If nothing changes after signing in, this account has nothing to read here.',
    back: 'Back to the course',
  },

  /* --- The queue --------------------------------------------------------- */
  queue: {
    title: 'Queue',
    waiting: (n: number) => (n === 1 ? '1 waiting' : `${n} waiting`),
    open: (n: number) => (n === 1 ? '1 open' : `${n} open`),
    mine: (n: number) => (n === 1 ? '1 with you' : `${n} with you`),
    /** The three filters over the same list. */
    allTab: 'All',
    waitingTab: 'Waiting',
    mineTab: 'With me',
    empty: 'Nothing is waiting.',
    emptyHint: 'Anything new lands here on its own.',
    emptyFiltered: 'Nothing under this filter.',
    unclaimed: 'Unclaimed',
    heldBy: (who: string) => `Held by ${who}`,
    heldByYou: 'Held by you',
    waitingFlag: 'Waiting',
    resolvedFlag: 'Resolved',
    noSubject: 'No subject',
    refresh: 'Read the queue again',
    offline: 'The desk could not be reached. Still trying.',
  },

  /** What the learner said the conversation is about. The list is closed. */
  topics: {
    access: 'Access',
    billing: 'Payment',
    tasks: 'Paid tasks',
    content: 'Course content',
    bug: 'Something broken',
    other: 'Something else',
  } as Record<SupportTopic, string>,

  /* --- Who else is on ---------------------------------------------------- */
  agents: {
    title: 'On shift',
    /** Reading the queue is clocking on; there is no separate heartbeat. */
    note: 'Reading the queue is how you appear here.',
    you: 'you',
    away: 'Away',
    holding: (n: number) => (n === 1 ? 'holding 1' : `holding ${n}`),
    alone: 'Nobody else is reading the queue right now.',
  },

  /* --- Being told something arrived -------------------------------------- */
  alerts: {
    title: 'Alerts',
    ask: 'Let this page raise notifications',
    on: 'Notifications are on for this browser.',
    refused: 'This browser has refused notifications. Its own settings are the only way back.',
    missing: 'This browser does not offer notifications.',
    /** The notification itself, and the only thing it is allowed to say. */
    heading: 'Somebody is waiting',
    body: (n: number) => (n === 1 ? '1 conversation is waiting for an answer.' : `${n} conversations are waiting for an answer.`),
  },

  /* --- The conversation -------------------------------------------------- */
  thread: {
    none: 'Nothing is open.',
    noneHint: 'Choose a conversation from the queue and it opens here.',
    connecting: 'Connecting…',
    reconnecting: 'Lost the connection. Trying again.',
    claim: 'Take this one',
    claiming: 'Taking…',
    release: 'Put it back',
    resolve: 'Mark it answered',
    reopen: 'Open it again',
    held: (who: string) => `${who} is already on this one.`,
    takeFirst: 'Take this conversation before you write in it.',
    resolvedNote: 'This was marked answered. Writing in it does not reopen it; the learner writing does.',
    placeholder: 'Write a reply',
    send: 'Send',
    empty: 'Nothing has been said yet.',
    removed: 'These words are gone.',
    earlier: 'Read what came before',
    loadingEarlier: 'Reading…',
    failed: 'Not sent.',
    retry: 'Send it again',
    opened: (when: string) => `Opened ${when}`,
    /** The line under the composer. It is about the customer, not the agent. */
    theyRead: (lang: string) => `They opened this in ${lang}.`,
    system: 'the course',
  },

  /* --- Who this is ------------------------------------------------------- */
  customer: {
    title: 'Who this is',
    unknown: 'This account is gone. The conversation is still readable.',
    paid: 'Has bought the course',
    unpaid: 'Has not bought the course',
    grantedBy: (source: string, when: string) => `Through ${source}, ${when}`,
    joined: (when: string) => `Account made ${when}`,
    seen: (when: string) => `Last here ${when}`,
    readsIn: 'Reads the course in',
    levels: (n: number) => (n === 1 ? '1 level cleared' : `${n} levels cleared`),
    stars: (n: number) => (n === 1 ? '1 star' : `${n} stars`),
    xp: (n: number) => `${n} XP`,
    checkpoints: (n: number) => (n === 1 ? '1 checkpoint confirmed' : `${n} checkpoints confirmed`),
    noRecord: 'Has not opened a level yet.',
    team: 'Team',
    noTeam: 'Not in a team.',
    tag: 'Chat tag',
    tagNote: 'The name moderation goes by, at a terminal. Nothing on this page can hide, ban or purge anything.',
    localeNames: { en: 'English', ar: 'Arabic' } as Record<'en' | 'ar', string>,
  },

  /* --- Notes only staff see ---------------------------------------------- */
  notes: {
    title: 'Internal notes',
    lede: 'Only the desk reads these. They are not in the conversation and the learner never sees them.',
    placeholder: 'Leave a note for whoever picks this up next',
    add: 'Add the note',
    adding: 'Adding…',
    empty: 'No notes on this one.',
    tooLong: (max: number) => `A note stops at ${max} characters.`,
    failed: 'The note was not saved. Try it again.',
  },

  /* --- Saved answers ----------------------------------------------------- */
  replies: {
    title: 'Saved answers',
    lede: 'Each one is written twice. The console inserts the half that matches the language the conversation was opened in.',
    none: 'Nothing saved yet.',
    insert: 'Put it in the box',
    inserted: 'Put in the box. Read it before you send it.',
    write: 'Write a new one',
    close: 'Never mind',
    fieldTitle: 'What to call it',
    fieldEn: 'In English',
    fieldAr: 'In Arabic',
    save: 'Save it',
    saving: 'Saving…',
    saved: 'Saved.',
    remove: 'Delete',
    removed: 'Deleted.',
    needBoth: 'Both languages have to be written before it can be saved.',
    full: (max: number) => `This deployment keeps ${max} saved answers, and that is all of them. Delete one first.`,
    failed: 'That could not be saved. Try it again.',
  },

  /* --- The one-column layout a phone gets -------------------------------- */
  panes: {
    queue: 'Queue',
    thread: 'Conversation',
    context: 'Context',
  },

  /* --- Time -------------------------------------------------------------- */
  waited: waitedEn,

  /* --- Failures the page says out loud ----------------------------------- */
  failedGeneric: 'That did not go through. Try it again in a moment.',
  failedGone: 'This is no longer yours to change. The queue has been read again.',
};

export const ar: typeof en = {
  /* --- The page itself --------------------------------------------------- */
  title: 'مكتب الدعم',
  kicker: 'للعاملين',
  landmark: 'مكتب الدعم: قائمة الانتظار، والمحادثة، ومن تحدّثه.',

  /* --- Signed out, or not on the roster ---------------------------------- */
  closed: {
    title: 'مكتب الدعم',
    lede: 'هنا يقرأ من يجيبون عن أسئلة هذه الدورة ما يُسأل فيها.',
    signIn: 'تسجيل الدخول',
    busy: 'جارٍ تسجيل الدخول…',
    note: 'إن لم يتغيّر شيء بعد تسجيل الدخول فليس لهذا الحساب ما يقرؤه هنا.',
    back: 'العودة إلى الدورة',
  },

  /* --- The queue --------------------------------------------------------- */
  queue: {
    title: 'قائمة الانتظار',
    waiting: (n: number) =>
      plural(n, {
        zero: 'لا محادثة تنتظر',
        one: 'محادثة واحدة تنتظر',
        two: 'محادثتان تنتظران',
        few: `${n} محادثات تنتظر`,
        many: `${n} محادثةً تنتظر`,
        other: `${n} محادثة تنتظر`,
      }),
    open: (n: number) =>
      plural(n, {
        zero: 'لا محادثة مفتوحة',
        one: 'محادثة واحدة مفتوحة',
        two: 'محادثتان مفتوحتان',
        few: `${n} محادثات مفتوحة`,
        many: `${n} محادثةً مفتوحة`,
        other: `${n} محادثة مفتوحة`,
      }),
    mine: (n: number) =>
      plural(n, {
        zero: 'لا شيء معك',
        one: 'واحدة معك',
        two: 'اثنتان معك',
        few: `${n} معك`,
        many: `${n} معك`,
        other: `${n} معك`,
      }),
    allTab: 'الكل',
    waitingTab: 'المنتظِرة',
    mineTab: 'معي',
    empty: 'لا شيء ينتظر.',
    emptyHint: 'كل جديد يظهر هنا من تلقاء نفسه.',
    emptyFiltered: 'لا شيء تحت هذا التصفية.',
    unclaimed: 'بلا صاحب',
    heldBy: (who: string) => `مع ${who}`,
    heldByYou: 'معك أنت',
    waitingFlag: 'تنتظر',
    resolvedFlag: 'أُغلقت',
    noSubject: 'بلا عنوان',
    refresh: 'اقرأ القائمة من جديد',
    offline: 'تعذّر الوصول إلى المكتب. والمحاولة مستمرة.',
  },

  topics: {
    access: 'الوصول',
    billing: 'الدفع',
    tasks: 'المهامّ المدفوعة',
    content: 'محتوى الدورة',
    bug: 'شيء معطوب',
    other: 'شيء آخر',
  } as Record<SupportTopic, string>,

  /* --- Who else is on ---------------------------------------------------- */
  agents: {
    title: 'على المناوبة',
    note: 'قراءتك للقائمة هي التي تُظهرك هنا.',
    you: 'أنت',
    away: 'غائب',
    holding: (n: number) =>
      plural(n, {
        zero: 'لا شيء بين يديه',
        one: 'محادثة واحدة بين يديه',
        two: 'محادثتان بين يديه',
        few: `${n} محادثات بين يديه`,
        many: `${n} محادثةً بين يديه`,
        other: `${n} محادثة بين يديه`,
      }),
    alone: 'لا أحد غيرك يقرأ القائمة الآن.',
  },

  /* --- Being told something arrived -------------------------------------- */
  alerts: {
    title: 'التنبيهات',
    ask: 'اسمح لهذه الصفحة بالتنبيه',
    on: 'التنبيهات مفعّلة في هذا المتصفح.',
    refused: 'رفض هذا المتصفح التنبيهات، ولا سبيل إلى عودتها إلا من إعداداته هو.',
    missing: 'لا يوفّر هذا المتصفح تنبيهات.',
    heading: 'أحدهم ينتظر',
    body: (n: number) =>
      plural(n, {
        zero: 'لا محادثة تنتظر جوابًا.',
        one: 'محادثة واحدة تنتظر جوابًا.',
        two: 'محادثتان تنتظران جوابًا.',
        few: `${n} محادثات تنتظر جوابًا.`,
        many: `${n} محادثةً تنتظر جوابًا.`,
        other: `${n} محادثة تنتظر جوابًا.`,
      }),
  },

  /* --- The conversation -------------------------------------------------- */
  thread: {
    none: 'لا شيء مفتوح.',
    noneHint: 'اختر محادثة من القائمة تُفتح هنا.',
    connecting: 'جارٍ الاتصال…',
    reconnecting: 'انقطع الاتصال. وتجري إعادة المحاولة.',
    claim: 'استلم هذه',
    claiming: 'جارٍ الاستلام…',
    release: 'أعدها إلى القائمة',
    resolve: 'أغلقها بالجواب',
    reopen: 'افتحها من جديد',
    held: (who: string) => `${who} يعمل عليها بالفعل.`,
    takeFirst: 'استلم المحادثة قبل أن تكتب فيها.',
    resolvedNote: 'أُغلقت هذه بالجواب. وكتابتك فيها لا تعيد فتحها، وإنما كتابة المتعلّم.',
    placeholder: 'اكتب الجواب',
    send: 'أرسل',
    empty: 'لم يُقل شيء بعد.',
    removed: 'زال هذا الكلام.',
    earlier: 'اقرأ ما قبله',
    loadingEarlier: 'جارٍ القراءة…',
    failed: 'لم يُرسل.',
    retry: 'أرسله مرّة أخرى',
    opened: (when: string) => `فُتحت ${when}`,
    theyRead: (lang: string) => `فتحها بـ${lang}.`,
    system: 'الدورة',
  },

  /* --- Who this is ------------------------------------------------------- */
  customer: {
    title: 'من هذا',
    unknown: 'زال هذا الحساب. والمحادثة تبقى مقروءة.',
    paid: 'اشترى الدورة',
    unpaid: 'لم يشترِ الدورة',
    grantedBy: (source: string, when: string) => `عن طريق ${source}، ${when}`,
    joined: (when: string) => `أُنشئ الحساب ${when}`,
    seen: (when: string) => `آخر حضور ${when}`,
    readsIn: 'يقرأ الدورة بـ',
    levels: (n: number) =>
      plural(n, {
        zero: 'لم يجتز مستوى',
        one: 'اجتاز مستوى واحدًا',
        two: 'اجتاز مستويين',
        few: `اجتاز ${n} مستويات`,
        many: `اجتاز ${n} مستوى`,
        other: `اجتاز ${n} مستوى`,
      }),
    stars: (n: number) =>
      plural(n, {
        zero: 'لا نجمة',
        one: 'نجمة واحدة',
        two: 'نجمتان',
        few: `${n} نجمات`,
        many: `${n} نجمةً`,
        other: `${n} نجمة`,
      }),
    xp: (n: number) => `${n} من نقاط الخبرة`,
    checkpoints: (n: number) =>
      plural(n, {
        zero: 'لا محطة مؤكَّدة',
        one: 'محطة واحدة مؤكَّدة',
        two: 'محطتان مؤكَّدتان',
        few: `${n} محطات مؤكَّدة`,
        many: `${n} محطةً مؤكَّدة`,
        other: `${n} محطة مؤكَّدة`,
      }),
    noRecord: 'لم يفتح مستوى بعد.',
    team: 'الفريق',
    noTeam: 'ليس في فريق.',
    tag: 'وسم المحادثة',
    tagNote: 'هو الاسم الذي يجري به الضبط من الـ terminal. ولا تملك هذه الصفحة إخفاءً ولا حظرًا ولا محوًا.',
    localeNames: { en: 'الإنجليزية', ar: 'العربية' } as Record<'en' | 'ar', string>,
  },

  /* --- Notes only staff see ---------------------------------------------- */
  notes: {
    title: 'ملاحظات داخلية',
    lede: 'لا يقرأ هذه إلا المكتب. وهي ليست في المحادثة ولا يراها المتعلّم أبدًا.',
    placeholder: 'اترك ملاحظة لمن يستلمها بعدك',
    add: 'أضف الملاحظة',
    adding: 'جارٍ الإضافة…',
    empty: 'لا ملاحظات على هذه.',
    tooLong: (max: number) => `الملاحظة تقف عند ${max} حرفًا.`,
    failed: 'لم تُحفظ الملاحظة. أعد المحاولة.',
  },

  /* --- Saved answers ----------------------------------------------------- */
  replies: {
    title: 'أجوبة محفوظة',
    lede: 'كل جواب مكتوب مرّتين، ويُدرج النصف الموافق للغة التي فُتحت بها المحادثة.',
    none: 'لا شيء محفوظ بعد.',
    insert: 'أدرجه في الصندوق',
    inserted: 'أُدرج في الصندوق. اقرأه قبل أن ترسله.',
    write: 'اكتب جوابًا جديدًا',
    close: 'اتركه',
    fieldTitle: 'بماذا تسمّيه',
    fieldEn: 'بالإنجليزية',
    fieldAr: 'بالعربية',
    save: 'احفظه',
    saving: 'جارٍ الحفظ…',
    saved: 'حُفظ.',
    remove: 'احذفه',
    removed: 'حُذف.',
    needBoth: 'لا بدّ من كتابة اللغتين قبل الحفظ.',
    full: (max: number) => `يحتفظ هذا النشر بـ${max} جوابًا محفوظًا، وقد اكتملت. احذف واحدًا أولًا.`,
    failed: 'تعذّر الحفظ. أعد المحاولة.',
  },

  /* --- The one-column layout a phone gets -------------------------------- */
  panes: {
    queue: 'القائمة',
    thread: 'المحادثة',
    context: 'السياق',
  },

  /* --- Time -------------------------------------------------------------- */
  waited: waitedAr,

  /* --- Failures the page says out loud ----------------------------------- */
  failedGeneric: 'لم يتم ذلك. أعد المحاولة بعد لحظة.',
  failedGone: 'لم يعد هذا لك لتغيّره. وقد أُعيدت قراءة القائمة.',
};

/**
 * The two numbers the copy above quotes, re-exported so a component never
 * writes one itself.
 *
 * A limit written into a sentence is a limit that lies the day it moves, and
 * these two are said in the copy *and* enforced on the field — the note's
 * `maxLength`, and the count of saved answers a deployment keeps. One import,
 * from the package the Worker checks against.
 */
export const LIMITS = { note: SUPPORT_LIMITS.note, replies: SUPPORT_LIMITS.replies };

function waitedDe(minutes: number): string {
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 Stunde' : `${hours} Stunden`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 Tag' : `${days} Tage`;
}

/**
 * The desk in German, for staff who read it in German. A conversation is
 * still recorded as English or Arabic (worker/support.ts keeps those two), so
 * `localeNames` names those two and saved answers are still written twice.
 */
export const de: typeof en = {
  title: 'Support-Desk',
  kicker: 'Team',
  landmark: 'Der Support-Desk: die Warteschlange, das Gespräch und mit wem du sprichst.',
  closed: {
    title: 'Support-Desk',
    lede: 'Unter dieser Adresse lesen die Leute, die Fragen zu diesem Kurs beantworten, diese Fragen.',
    signIn: 'Anmelden',
    busy: 'Anmeldung läuft …',
    note: 'Wenn sich nach dem Anmelden nichts ändert, hat dieses Konto hier nichts zu lesen.',
    back: 'Zurück zum Kurs',
  },
  queue: {
    title: 'Warteschlange',
    waiting: (n) => `${n} wartend`,
    open: (n) => `${n} offen`,
    mine: (n) => `${n} bei dir`,
    allTab: 'Alle',
    waitingTab: 'Wartend',
    mineTab: 'Bei mir',
    empty: 'Niemand wartet.',
    emptyHint: 'Alles Neue landet von selbst hier.',
    emptyFiltered: 'Nichts unter diesem Filter.',
    unclaimed: 'Nicht übernommen',
    heldBy: (who) => `Übernommen von ${who}`,
    heldByYou: 'Von dir übernommen',
    waitingFlag: 'Wartend',
    resolvedFlag: 'Erledigt',
    noSubject: 'Kein Betreff',
    refresh: 'Warteschlange neu lesen',
    offline: 'Der Desk war nicht erreichbar. Wird weiter versucht.',
  },
  topics: {
    access: 'Zugang',
    billing: 'Zahlung',
    tasks: 'Bezahlte Aufgaben',
    content: 'Kursinhalt',
    bug: 'Etwas ist kaputt',
    other: 'Etwas anderes',
  },
  agents: {
    title: 'Im Dienst',
    note: 'Wer die Warteschlange liest, erscheint hier.',
    you: 'du',
    away: 'Abwesend',
    holding: (n) => `hält ${n}`,
    alone: 'Gerade liest sonst niemand die Warteschlange.',
  },
  alerts: {
    title: 'Benachrichtigungen',
    ask: 'Diese Seite Benachrichtigungen anzeigen lassen',
    on: 'Benachrichtigungen sind für diesen Browser an.',
    refused: 'Dieser Browser hat Benachrichtigungen abgelehnt. Nur seine eigenen Einstellungen führen zurück.',
    missing: 'Dieser Browser bietet keine Benachrichtigungen.',
    heading: 'Jemand wartet',
    body: (n) => (n === 1 ? '1 Gespräch wartet auf eine Antwort.' : `${n} Gespräche warten auf eine Antwort.`),
  },
  thread: {
    none: 'Nichts ist geöffnet.',
    noneHint: 'Wähl ein Gespräch aus der Warteschlange, dann öffnet es sich hier.',
    connecting: 'Verbinde …',
    reconnecting: 'Verbindung verloren. Wird erneut versucht.',
    claim: 'Übernehmen',
    claiming: 'Wird übernommen …',
    release: 'Zurücklegen',
    resolve: 'Als beantwortet markieren',
    reopen: 'Wieder öffnen',
    held: (who) => `${who} kümmert sich schon darum.`,
    takeFirst: 'Übernimm dieses Gespräch, bevor du darin schreibst.',
    resolvedNote: 'Das wurde als beantwortet markiert. Wenn du darin schreibst, öffnet es sich nicht wieder; wenn die Person schreibt, schon.',
    placeholder: 'Antwort schreiben',
    send: 'Senden',
    empty: 'Noch wurde nichts gesagt.',
    removed: 'Diese Worte sind weg.',
    earlier: 'Lesen, was davor kam',
    loadingEarlier: 'Wird gelesen …',
    failed: 'Nicht gesendet.',
    retry: 'Erneut senden',
    opened: (when) => `Geöffnet ${when}`,
    theyRead: (lang) => `Die Person hat das auf ${lang} geöffnet.`,
    system: 'der Kurs',
  },
  customer: {
    title: 'Wer das ist',
    unknown: 'Dieses Konto gibt es nicht mehr. Das Gespräch ist weiterhin lesbar.',
    paid: 'Hat den Kurs gekauft',
    unpaid: 'Hat den Kurs nicht gekauft',
    grantedBy: (source, when) => `Über ${source}, ${when}`,
    joined: (when) => `Konto angelegt ${when}`,
    seen: (when) => `Zuletzt hier ${when}`,
    readsIn: 'Liest den Kurs auf',
    levels: (n) => `${n} Level geschafft`,
    stars: (n) => (n === 1 ? '1 Stern' : `${n} Sterne`),
    xp: (n) => `${n} XP`,
    checkpoints: (n) => (n === 1 ? '1 Checkpoint bestätigt' : `${n} Checkpoints bestätigt`),
    noRecord: 'Hat noch kein Level geöffnet.',
    team: 'Team',
    noTeam: 'In keinem Team.',
    tag: 'Chat-Tag',
    tagNote: 'Der Name, unter dem die Moderation am Terminal arbeitet. Nichts auf dieser Seite kann etwas ausblenden, sperren oder löschen.',
    localeNames: { en: 'Englisch', ar: 'Arabisch' },
  },
  notes: {
    title: 'Interne Notizen',
    lede: 'Nur der Desk liest diese. Sie stehen nicht im Gespräch, und die Person sieht sie nie.',
    placeholder: 'Hinterlass eine Notiz für die Person, die das als Nächstes übernimmt',
    add: 'Notiz hinzufügen',
    adding: 'Wird hinzugefügt …',
    empty: 'Keine Notizen dazu.',
    tooLong: (max) => `Eine Notiz endet bei ${max} Zeichen.`,
    failed: 'Die Notiz wurde nicht gespeichert. Versuch es noch einmal.',
  },
  replies: {
    title: 'Gespeicherte Antworten',
    lede: 'Jede wird zweimal geschrieben. Die Konsole fügt die Hälfte ein, die zur Sprache passt, in der das Gespräch geöffnet wurde.',
    none: 'Noch nichts gespeichert.',
    insert: 'Ins Feld setzen',
    inserted: 'Ins Feld gesetzt. Lies sie, bevor du sie sendest.',
    write: 'Neue schreiben',
    close: 'Doch nicht',
    fieldTitle: 'Wie sie heißen soll',
    fieldEn: 'Auf Englisch',
    fieldAr: 'Auf Arabisch',
    save: 'Speichern',
    saving: 'Wird gespeichert …',
    saved: 'Gespeichert.',
    remove: 'Löschen',
    removed: 'Gelöscht.',
    needBoth: 'Beide Sprachen müssen geschrieben sein, bevor sie gespeichert werden kann.',
    full: (max) => `Diese Installation hält ${max} gespeicherte Antworten, und das sind alle. Lösch zuerst eine.`,
    failed: 'Das konnte nicht gespeichert werden. Versuch es noch einmal.',
  },
  panes: {
    queue: 'Warteschlange',
    thread: 'Gespräch',
    context: 'Kontext',
  },
  waited: waitedDe,
  failedGeneric: 'Das hat nicht geklappt. Versuch es gleich noch einmal.',
  failedGone: 'Das kannst du nicht mehr ändern. Die Warteschlange wurde neu gelesen.',
};

function jaWaited(minutes: number): string {
  if (minutes < 1) return 'たった今';
  if (minutes < 60) return `${minutes}分`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}時間`;
  const days = Math.floor(hours / 24);
  return `${days}日`;
}

/**
 * The desk in Japanese, for staff who read it in Japanese. A conversation is
 * still recorded as English or Arabic (worker/support.ts keeps those two), so
 * `localeNames` names those two and saved answers are still written twice.
 */
export const ja: typeof en = {
  title: 'サポートデスク',
  kicker: 'スタッフ',
  landmark: 'サポートデスク: キュー、会話、そして話している相手。',
  closed: {
    title: 'サポートデスク',
    lede: 'このアドレスは、コースについての質問に答える担当者が、質問を読む場所です。',
    signIn: 'ログイン',
    busy: 'ログイン中…',
    note: 'ログインしても何も変わらない場合、このアカウントにはここで読めるものがありません。',
    back: 'コースに戻る',
  },
  queue: {
    title: 'キュー',
    waiting: (n: number) => `${n}件待機中`,
    open: (n: number) => `${n}件オープン`,
    mine: (n: number) => `${n}件を担当中`,
    allTab: 'すべて',
    waitingTab: '待機中',
    mineTab: '自分の担当',
    empty: '待機中のものはありません。',
    emptyHint: '新しいものは自動でここに届きます。',
    emptyFiltered: 'このフィルターに該当するものはありません。',
    unclaimed: '未担当',
    heldBy: (who: string) => `担当: ${who}`,
    heldByYou: '担当: あなた',
    waitingFlag: '待機中',
    resolvedFlag: '解決済み',
    noSubject: '件名なし',
    refresh: 'キューを再読み込み',
    offline: 'デスクに接続できませんでした。再試行しています。',
  },
  topics: {
    access: 'アクセス',
    billing: 'お支払い',
    tasks: '有償タスク',
    content: 'コースの内容',
    bug: '不具合',
    other: 'その他',
  },
  agents: {
    title: '対応中',
    note: 'キューを読んでいる人が、ここに表示されます。',
    you: 'あなた',
    away: '不在',
    holding: (n: number) => `${n}件を担当`,
    alone: '今、キューを読んでいるのはあなただけです。',
  },
  alerts: {
    title: '通知',
    ask: 'このページからの通知を許可する',
    on: 'このブラウザーでは通知がオンです。',
    refused: 'このブラウザーは通知を拒否しています。元に戻せるのは、ブラウザー自身の設定だけです。',
    missing: 'このブラウザーは通知に対応していません。',
    heading: '返信を待っている人がいます',
    body: (n: number) => (n === 1 ? '1件の会話が返信を待っています。' : `${n}件の会話が返信を待っています。`),
  },
  thread: {
    none: '開いている会話はありません。',
    noneHint: 'キューから会話を選ぶと、ここに開きます。',
    connecting: '接続中…',
    reconnecting: '接続が切れました。再試行しています。',
    claim: '担当する',
    claiming: '担当を設定中…',
    release: '担当を外す',
    resolve: '回答済みにする',
    reopen: '再度開く',
    held: (who: string) => `${who}がすでに対応しています。`,
    takeFirst: '書き込む前に、この会話を担当してください。',
    resolvedNote: 'この会話は回答済みになっています。ここに書き込んでも再オープンはされません。再オープンされるのは、学習者が書き込んだときです。',
    placeholder: '返信を書く',
    send: '送信',
    empty: 'まだ何も書き込まれていません。',
    removed: 'この内容は削除されました。',
    earlier: 'これより前を読む',
    loadingEarlier: '読み込み中…',
    failed: '送信されませんでした。',
    retry: '再送信',
    opened: (when: string) => `${when}に開始`,
    theyRead: (lang: string) => `相手は${lang}で開きました。`,
    system: 'コース',
  },
  customer: {
    title: '相手について',
    unknown: 'このアカウントは存在しません。会話は引き続き読めます。',
    paid: 'コースを購入済み',
    unpaid: 'コースは未購入',
    grantedBy: (source: string, when: string) => `${source}経由、${when}`,
    joined: (when: string) => `${when}にアカウント作成`,
    seen: (when: string) => `最終アクセス: ${when}`,
    readsIn: 'コースを読む言語',
    levels: (n: number) => `${n}レベルをクリア`,
    stars: (n: number) => `星${n}個`,
    xp: (n: number) => `${n} XP`,
    checkpoints: (n: number) => `${n}個のチェックポイントを検証済み`,
    noRecord: 'まだレベルを開いていません。',
    team: 'チーム',
    noTeam: 'チームには入っていません。',
    tag: 'チャットのタグ',
    tagNote: 'モデレーションが、ターミナルで使う名前です。このページからは、非表示、BAN、削除のいずれもできません。',
    localeNames: { en: '英語', ar: 'アラビア語' },
  },
  notes: {
    title: '社内メモ',
    lede: 'これを読めるのはデスクだけです。会話には含まれず、学習者に表示されることもありません。',
    placeholder: '次に担当する人へのメモを残す',
    add: 'メモを追加',
    adding: '追加中…',
    empty: 'この件のメモはありません。',
    tooLong: (max: number) => `メモは${max}文字までです。`,
    failed: 'メモを保存できませんでした。もう一度お試しください。',
  },
  replies: {
    title: '保存済みの回答',
    lede: '回答はそれぞれ2言語で書きます。コンソールは、会話が開かれた言語に合うほうを挿入します。',
    none: '保存されたものはまだありません。',
    insert: '入力欄に入れる',
    inserted: '入力欄に入れました。送信する前に読み返してください。',
    write: '新しく書く',
    close: 'やめる',
    fieldTitle: '名前',
    fieldEn: '英語',
    fieldAr: 'アラビア語',
    save: '保存',
    saving: '保存中…',
    saved: '保存しました。',
    remove: '削除',
    removed: '削除しました。',
    needBoth: '保存するには、2言語とも書く必要があります。',
    full: (max: number) => `この環境で保存できる回答は${max}件で、すべて使われています。先に1件削除してください。`,
    failed: '保存できませんでした。もう一度お試しください。',
  },
  panes: {
    queue: 'キュー',
    thread: '会話',
    context: 'コンテキスト',
  },
  waited: jaWaited,
  failedGeneric: '送信できませんでした。少し待ってから、もう一度お試しください。',
  failedGone: 'これはもうあなたが変更できるものではありません。キューを再読み込みしました。',
};
